export type FieldVisibilityEffect = 'show' | 'hide';
export type FieldVisibilityCombinator = 'AND' | 'OR';

export interface FieldVisibilityClause {
    entityId: string;
    fieldId: string;
    datatype?: string;
    operator: string;
    values: string[];
}

export interface FieldVisibilityRule {
    type?: string;
    effect: FieldVisibilityEffect;
    combinator: FieldVisibilityCombinator;
    clauses: FieldVisibilityClause[];
}

export type FieldVisibilityRulesMap = { [controlledFieldUid: string]: FieldVisibilityRule };

export type FieldValueResolver = (entityUid: string, fieldUid: string) => unknown;

export type FieldExistenceChecker = (entityUid: string, fieldUid: string) => boolean;

export const extractFieldVisibilityRules = (configurator: any): FieldVisibilityRulesMap => {
    const rules = configurator?.rules ?? configurator?.response?.rules;
    return rules && typeof rules === 'object' && !Array.isArray(rules) ? rules : {};
};

export const buildModelFieldExistenceChecker = (assetMetaData: any): FieldExistenceChecker | undefined => {
    const fieldUidsByEntityUid: { [entityUid: string]: Set<string> } = {};
    const indexEntities = (entities: any[]) =>
        (entities || []).forEach((entity: any) => {
            if (entity?.uid) {
                const uids = fieldUidsByEntityUid[entity.uid] || (fieldUidsByEntityUid[entity.uid] = new Set<string>());
                (entity?.fields || []).forEach((field: any) => field?.uid && uids.add(field.uid));
            }
            indexEntities(entity?.entities);
        });
    indexEntities(assetMetaData?.entities);
    if (!Object.keys(fieldUidsByEntityUid).length) {
        return undefined;
    }
    return (entityUid, fieldUid) => {
        if (!fieldUid) {
            return false;
        }
        if (entityUid) {
            return !!fieldUidsByEntityUid[entityUid]?.has(fieldUid);
        }
        return Object.keys(fieldUidsByEntityUid).some(uid => fieldUidsByEntityUid[uid].has(fieldUid));
    };
};

const evaluateClause = (clause: FieldVisibilityClause, resolve: FieldValueResolver): boolean => {
    const actual = resolve(clause.entityId, clause.fieldId);
    const s = actual == null ? '' : String(actual);
    const v = clause.values || [];
    switch (clause.operator) {
        case 'equals':
            return s === v[0];
        case 'notEquals':
            return s !== v[0];
        case 'contains':
            return s.includes(v[0] ?? '');
        case 'notContains':
            return !s.includes(v[0] ?? '');
        case 'startsWith':
            return s.startsWith(v[0] ?? '');
        case 'endsWith':
            return s.endsWith(v[0] ?? '');
        case 'greaterThan':
            return Number(actual) > Number(v[0]);
        case 'greaterOrEqual':
            return Number(actual) >= Number(v[0]);
        case 'lessThan':
            return Number(actual) < Number(v[0]);
        case 'lessOrEqual':
            return Number(actual) <= Number(v[0]);
        case 'between':
            return Number(actual) >= Number(v[0]) && Number(actual) <= Number(v[1]);
        case 'in':
            return v.includes(s);
        case 'notIn':
            return !v.includes(s);
        case 'isEmpty':
            return s === '';
        case 'isNotEmpty':
            return s !== '';
        case 'isTrue':
            return actual === true || s === 'true';
        case 'isFalse':
            return actual === false || s === 'false';
        default:
            return false;
    }
};

const evaluateClauses = (clauses: FieldVisibilityClause[], combinator: FieldVisibilityCombinator, resolve: FieldValueResolver): boolean => {
    if (!clauses.length) return true;
    const results = clauses.map(c => evaluateClause(c, resolve));
    return combinator === 'OR' ? results.some(Boolean) : results.every(Boolean);
};

const resolveApplicableClauses = (rule: FieldVisibilityRule, exists?: FieldExistenceChecker): FieldVisibilityClause[] | null => {
    if (!exists) return rule.clauses;
    const live = rule.clauses.filter(c => exists(c.entityId, c.fieldId));
    if (live.length === rule.clauses.length) return rule.clauses;
    if (rule.combinator === 'OR') return live.length ? live : null;
    return null;
};

export const isFieldVisibleByRule = (
    rule: FieldVisibilityRule | undefined,
    resolve: FieldValueResolver,
    exists?: FieldExistenceChecker
): boolean => {
    if (!rule || !rule.clauses?.length) return true;
    const clauses = resolveApplicableClauses(rule, exists);
    if (!clauses) return true;
    const matched = evaluateClauses(clauses, rule.combinator, resolve);
    return rule.effect === 'hide' ? !matched : matched;
};
